import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PlatformFooter from '../components/PlatformFooter';

/**
 * LegalLayout — shared editorial chrome for Impressum / Privacy / Terms.
 *
 * Why a shared layout?
 *   The three pages should feel like one quiet booklet — same paper colour,
 *   same narrow reading column, same calm typography. Pulling the wrapper
 *   out keeps each page focused on its own copy.
 */
const LINKS = [
  { to: '/impressum', label: 'Impressum' },
  { to: '/privacy',   label: 'Privacy' },
  { to: '/terms',     label: 'Terms' },
];

const LegalLayout = ({ title, eyebrow, intro, lastUpdated, children, testId }) => {
  const { pathname } = useLocation();

  // SEO-friendly page titles.
  useEffect(() => {
    if (title) document.title = `${title} — Maestra`;
  }, [title]);

  // Render the "Last updated on …" line in a soft, human format
  // (e.g. "11 February 2026"). The ISO string comes from the backend.
  const lastUpdatedHuman = (() => {
    if (!lastUpdated) return null;
    try {
      const d = new Date(lastUpdated);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return null;
    }
  })();

  return (
    <div
      data-testid={testId}
      className="legal-page"
      style={{
        minHeight: '100vh',
        background: 'var(--kahla-cream-light, #faf8f5)',
        color: 'var(--kahla-charcoal, #2a2a2a)',
        animation: 'legalFadeIn 480ms ease both',
      }}
    >
      <style>{`
        @keyframes legalFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .legal-page a { color: var(--kahla-charcoal, #2a2a2a); text-decoration: underline; text-underline-offset: 4px; text-decoration-color: rgba(166,152,136,0.55); transition: text-decoration-color 0.2s ease; }
        .legal-page a:hover { text-decoration-color: var(--kahla-charcoal, #2a2a2a); }
        .legal-page h2 {
          font-family: 'Playfair Display', serif;
          font-size: 18px; font-weight: 400;
          letter-spacing: -0.005em;
          color: var(--kahla-charcoal, #2a2a2a);
          margin: 38px 0 10px;
        }
        .legal-page p {
          font-family: inherit;
          font-size: 15px; line-height: 1.75;
          color: var(--kahla-text, #3a3a3a);
          margin: 0 0 14px;
        }
        .legal-page .legal-eyebrow {
          font-family: 'Playfair Display', serif;
          font-size: 11px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: var(--kahla-text-light);
          margin: 0 0 14px;
          font-weight: 400;
        }
        .legal-page .legal-title {
          font-family: 'Playfair Display', serif;
          font-size: 48px; font-weight: 400;
          letter-spacing: -0.012em;
          color: var(--kahla-charcoal, #2a2a2a);
          line-height: 1.12;
          margin: 0 0 26px;
        }
        .legal-page .legal-intro {
          font-family: 'Cormorant Garamond', 'Playfair Display', serif;
          font-style: italic;
          font-size: 19px; line-height: 1.7;
          color: var(--kahla-text-light);
          margin: 0 0 12px;
        }
        .legal-page .legal-divider {
          border: none;
          border-top: 1px solid var(--kahla-border, #e6dfd2);
          margin: 36px 0 0;
        }
        .legal-page .legal-label {
          font-size: 10px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--kahla-text-light);
          margin: 24px 0 6px;
          font-weight: 500;
        }
        .legal-page .legal-callout {
          font-family: 'Cormorant Garamond', 'Playfair Display', serif;
          font-style: italic;
          font-size: 17px; line-height: 1.7;
          color: var(--kahla-charcoal, #2a2a2a);
          padding: 22px 26px;
          border-left: 1px solid var(--kahla-border, #e6dfd2);
          margin: 32px 0;
          background: rgba(255, 247, 235, 0.45);
        }
        @media (max-width: 720px) {
          .legal-page .legal-title { font-size: 36px; }
          .legal-page h2 { font-size: 17px; }
          .legal-page p, .legal-page .legal-intro { font-size: 15px; }
        }
      `}</style>

      {/* Quiet header — brand mark + a single link back to the studio. */}
      <header
        style={{
          padding: '28px 32px 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          maxWidth: 1120, margin: '0 auto',
        }}
      >
        <Link
          to="/"
          data-testid="legal-home-link"
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 12, letterSpacing: '0.32em',
            textTransform: 'uppercase', textDecoration: 'none',
            color: 'var(--kahla-text-light)',
          }}
        >
          Maestra
        </Link>
        <Link
          to="/"
          style={{
            fontSize: 11, letterSpacing: '0.14em',
            textTransform: 'uppercase', textDecoration: 'underline',
            textUnderlineOffset: 4,
            color: 'var(--kahla-text-light)',
          }}
        >
          ← Back to home
        </Link>
      </header>

      {/* Narrow reading column. */}
      <main
        style={{
          maxWidth: 640, margin: '0 auto',
          padding: '64px 32px 80px',
        }}
      >
        {eyebrow && <p className="legal-eyebrow" data-testid="legal-eyebrow">{eyebrow}</p>}
        <h1 className="legal-title" data-testid="legal-title">{title}</h1>
        {intro && <p className="legal-intro" data-testid="legal-intro">{intro}</p>}

        <div data-testid="legal-body">
          {children}
        </div>

        {lastUpdatedHuman && (
          <p
            data-testid="legal-last-updated"
            style={{
              marginTop: 48,
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              opacity: 0.7,
            }}
          >
            Last updated · {lastUpdatedHuman}
          </p>
        )}
      </main>

      {/* Cross-doc nav + small brand line — same family as the global footer. */}
      <footer
        data-testid="legal-footer"
        style={{
          maxWidth: 640, margin: '0 auto',
          padding: '0 32px 80px',
          borderTop: '1px solid var(--kahla-border, #e6dfd2)',
          paddingTop: 28,
        }}
      >
        <nav
          aria-label="Legal pages"
          style={{
            display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap',
            marginBottom: 18,
          }}
        >
          {LINKS.map((l, i) => {
            const active = pathname === l.to;
            return (
              <React.Fragment key={l.to}>
                {i > 0 && <span aria-hidden="true" style={{ color: 'var(--kahla-text-light)', opacity: 0.55 }}>·</span>}
                <Link
                  to={l.to}
                  data-testid={`legal-link-${l.label.toLowerCase()}`}
                  style={{
                    fontSize: 11, letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    textDecoration: active ? 'none' : 'underline',
                    textUnderlineOffset: 4,
                    color: active ? 'var(--kahla-charcoal, #2a2a2a)' : 'var(--kahla-text-light)',
                    fontWeight: active ? 600 : 400,
                  }}
                >{l.label}</Link>
              </React.Fragment>
            );
          })}
        </nav>
        <PlatformFooter variant="compact" style={{
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
        }} />
      </footer>
    </div>
  );
};

export default LegalLayout;
